import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { validateImage } from "@/resources/attachment/imageValidator";
import type { Feature } from "@/resources/attachment/uploadToFirebaseStorage";
import { uploadToFirebaseStorage } from "@/resources/attachment/uploadToFirebaseStorage";

namespace useUploadImageMutation {
  export interface Options {
    onSuccess?: (downloadUrl: string) => void;
    onError?: (error: unknown) => void;
    onSettled?: VoidFunction;
  }
  export interface Params {
    feature: Feature;
    dialogueId: string;
    file: File;
  }
}
export function useUploadImageMutation({
  onSuccess,
  onError,
  onSettled,
}: useUploadImageMutation.Options) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      feature,
      dialogueId,
      file,
    }: useUploadImageMutation.Params) => {
      const validationError = await validateImage(feature, file);
      if (validationError) {
        enqueueSnackbar(validationError.reactNode, { variant: "error" });
        throw new Error("Validation failed");
      }

      const uploadedUrl = await uploadToFirebaseStorage({
        feature,
        pathParams: {
          dialogueId,
        },
        file,
      });

      return uploadedUrl.downloadUrl;
    },
    onSuccess: (downloadUrl) => {
      enqueueSnackbar(t("resource.attachment.uploadSuccess"), {
        variant: "success",
      });
      onSuccess?.(downloadUrl);
    },
    onError: (error) => {
      if (error instanceof Error) {
        enqueueSnackbar(t("resource.attachment.failedToUploadImage"), {
          variant: "error",
        });
      }
      onError?.(error);
    },
    onSettled: () => {
      onSettled?.();
    },
  });
}
